<template>
  <div class="lesson-preview-container" @click="handlePageClickClearTooltip">
    <!-- Loading Overlay -->
    <LoadingOverlay
      :model-value="isLoading"
      message="Loading Professional Development..."
      :full-screen="true"
      :teleport-to-body="true"
    />

    <!-- Header section -->
    <div class="lesson-content-wrapper">
      <div class="lesson-main-content">
        <div v-if="!isLoading && lessonData.title" class="lesson-header">
          <div class="lesson-header-content">
            <BreadCrumbs :items="breadcrumbItems" class="desktop-only" />
            <div class="lesson-title-container">
              <div class="title-wrapper">
                <h1 class="lesson-title">
                  {{ lessonData.title || "Untitled Lesson" }}
                </h1>
              </div>
              <div class="lesson-meta-container">
                <div class="lesson-meta">
                  <template v-if="software">
                    <div class="lesson-meta-item software-item">
                      <div class="lesson-meta-item-icon">
                        <img src="/images/icon/monitor.svg" alt="Software" />
                      </div>
                      <span class="software-text"
                        >Software: {{ software }}</span
                      >
                    </div>
                    <v-divider
                      vertical
                      class="mobile-divider desktop-only"
                    ></v-divider>
                  </template>

                  <div class="lesson-actions-wrapper">
                    <ExportLessonPdf
                      :lesson-data="lessonData"
                      :software="software"
                      :categories="categories"
                    />
                    <v-divider vertical class="mobile-divider"></v-divider>
                    <button
                      class="lesson-meta-item icon-only-action"
                      @click="likeLesson"
                      :aria-label="
                        lessonData.isLiked ? 'Unlike lesson' : 'Like lesson'
                      "
                      :aria-pressed="lessonData.isLiked"
                    >
                      <div class="lesson-meta-item-icon">
                        <div class="like-icon">
                          <BookmarkIcon color="#E00069" />
                        </div>
                      </div>
                    </button>
                    <v-divider vertical class="mobile-divider"></v-divider>
                    <button
                      class="lesson-meta-item icon-only-action"
                      aria-label="Share lesson"
                    >
                      <div class="lesson-meta-item-icon share-icon-wrap">
                        <ShareIcon />
                      </div>
                      <ShareMenu />
                    </button>

                    <v-divider vertical class="mobile-divider"></v-divider>
                    <div
                      class="comment-toggle-container action-comment-toggle mobile-only"
                    >
                      <div class="lesson-meta-item-icon">
                        <CommentVariantIcon />
                      </div>
                      <label class="switch switch-gray">
                        <input
                          type="checkbox"
                          v-model="isCommentingEnabled"
                          aria-label="Toggle Comments"
                        />
                        <span class="slider round"></span>
                      </label>
                    </div>
                  </div>
                </div>
                <div class="lesson-meta-wrapper desktop-only">
                  <div class="comment-toggle-container">
                    <div class="lesson-meta-item-icon">
                      <CommentVariantIcon />
                    </div>
                    <label class="switch">
                      <input
                        type="checkbox"
                        v-model="isCommentingEnabled"
                        aria-label="Toggle Comments"
                      />
                      <span class="slider round"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!isLoading && loadError">
          <ErrorState
            :message="loadError.message"
            :error-code="loadError.code"
            :actions="errorActions"
          />
        </div>

        <div
          class="lesson-main-container"
          v-if="!isLoading && lessonData.title"
        >
          <div class="lesson-content" :class="{ expanded: sidebarCollapsed }">
            <div
              class="lesson-tabs"
              role="tablist"
              aria-label="Professional Development Tabs"
            >
              <div
                v-for="(tab, index) in lessonData.tabs"
                :key="index"
                class="lesson-tab"
                :class="{ active: activeTabIndex === index }"
                @click="selectTab(index)"
                role="tab"
                :aria-selected="activeTabIndex === index"
                tabindex="0"
                @keydown.enter="selectTab(index)"
                @keydown.space.prevent="selectTab(index)"
              >
                {{ tab.name }}
                <div
                  v-if="tab.duration && activeTabIndex === index"
                  class="tab-duration"
                >
                  <img
                    src="/images/icon/clock.svg"
                    alt="Clock"
                    width="20"
                    height="20"
                  />
                  <span>{{ tab.duration }} Minutes</span>
                </div>
              </div>
              <div
                class="lesson-tab"
                :class="{ active: activeTabIndex === lessonData.tabs.length }"
                @click="selectTab(lessonData.tabs.length)"
                role="tab"
                :aria-selected="activeTabIndex === lessonData.tabs.length"
                tabindex="0"
                @keydown.enter="selectTab(lessonData.tabs.length)"
                @keydown.space.prevent="selectTab(lessonData.tabs.length)"
              >
                Comments
              </div>
            </div>

            <div
              class="lesson-tab-content"
              ref="lessonTabContentRef"
              v-if="lessonData.tabs && lessonData.tabs.length > 0"
              role="tabpanel"
              tabindex="0"
            >
              <template v-if="currentTab.type === 'assessment'">
                <AssessmentView :assessments="currentTab.assessments" />
              </template>
              <template v-else-if="activeTabIndex === lessonData.tabs.length">
                <ProfessionalDevelopmentComments :lesson-id="lessonData.id" />
              </template>
              <template v-else>
                <div
                  v-for="(section, sectionIndex) in getCurrentTabSections()"
                  :key="sectionIndex"
                  class="lesson-section"
                >
                  <div
                    v-if="section.content && section.content.trim() !== ''"
                    class="tiptap-content"
                    v-html="addTableHeaders(section.content)"
                  ></div>
                  <div v-else class="empty-section-state">
                    <div class="empty-section-icon">
                      <v-icon size="48" color="#ddd"
                        >mdi-file-document-outline</v-icon
                      >
                    </div>
                    <div class="empty-section-text">
                      <h3>No content available</h3>
                      <p>
                        This section is currently empty. Content will appear
                        here when it's added.
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div
          v-if="
            (lessonData.slide_url ||
              lessonData.lesson_instructional ||
              currentLessonId) &&
            activeTabIndex === 0
          "
          class="resources-section"
        >
          <h2>Resources</h2>
          <div class="resources-buttons">
            <!-- SCORM File Button -->
            <v-btn
              v-if="currentLessonId"
              class="resource-copy-button resource-copy-button--primary"
              size="large"
              @click="viewScormFile"
              :disabled="isLoadingScormUrl"
            >
              <v-icon start v-if="!isLoadingScormUrl">mdi-play-circle</v-icon>
              <span v-if="!isLoadingScormUrl">View SCORM File</span>
              <span v-else class="button-loader"></span>
            </v-btn>
            <v-btn
              v-if="lessonData.slide_url"
              class="resource-copy-button resource-copy-button--primary"
              size="large"
              @click="openCopySlideModal"
            >
              <v-icon start>mdi-content-copy</v-icon>
              <span>Copy Slide Deck to your Drive</span>
            </v-btn>
            <v-btn
              v-for="material in lessonData.lesson_instructional"
              :key="material.id || material.name"
              class="resource-copy-button resource-copy-button--secondary"
              size="large"
              @click="openCopyResourceModal(material)"
            >
              <v-icon start>mdi-content-copy</v-icon>
              <span>Copy {{ material.name }} to your Drive</span>
            </v-btn>
          </div>
        </div>
      </div>
      <div>
        <ViewProfessionalDevelopmentSidebar
          :categories="categories"
          v-model:collapsed="sidebarCollapsed"
          v-model:showMobile="showMobileSidebar"
          :is-authenticated="isAuthenticated"
          :progress="lessonData.progress"
          v-if="!loadError"
        />
      </div>
    </div>

    <!-- Commenting UI -->
    <Teleport
      :to="popoverHostSelector"
      :disabled="!showCommentPopover || !popoverHostSelector || isMobile"
    >
      <CommentPopover
        :visible="showCommentPopover"
        :target-block-id="activeBlockIdForPopover"
        :existing-comments="commentsForActiveBlock"
        :lesson-id="currentLessonId"
        :position="popoverPosition"
        :comment-service="commentService"
        @comments-updated="handleCommentsUpdated"
        @close="closeCommentPopover"
      />
    </Teleport>
    <CopySlideDeck
      :is-open="isCopySlideModalOpen"
      :file-url="currentCopyResource.url"
      :file-name="currentCopyResource.name"
      @update:is-open="isCopySlideModalOpen = $event"
    />

    <!-- SCORM Iframe Modal -->
    <div v-if="showScormIframe" class="scorm-iframe-modal">
      <div class="scorm-iframe-container">
        <div class="scorm-iframe-header">
          <v-btn @click="closeScormIframe" icon="mdi-close" size="small" />
        </div>
        <div v-if="isScormIframeLoading" class="scorm-iframe-loading">
          <div class="scorm-loader"></div>
          <p>Loading SCORM content...</p>
        </div>
        <iframe
          :src="scormIframeUrl"
          frameborder="0"
          allowfullscreen
          @load="scormIframeLoaded"
        ></iframe>
      </div>
    </div>

    <!-- Login Required Modal -->
    <LoginRequiredModal
      v-model="showLoginRequiredModal"
      title="Login to Access SCORM Content"
      description="Please Sign In or sign up to view interactive SCORM learning content and track your progress."
      :redirect-path="$route.path"
      @close="showLoginRequiredModal = false"
    />

    <!-- Mobile Sticky Footer -->
    <div class="mobile-sticky-footer mobile-only">
      <button class="mobile-details-btn-full" @click="handleShowMobileSidebar">
        View More Details
      </button>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
/* eslint-disable no-inner-declarations */
import CommentPopover from "@/components/comments/CommentPopover.vue";
import AssessmentView from "@/components/lesson-library/AssessmentView.vue";
import CopySlideDeck from "@/components/lesson-library/CopySlideDeck.vue";
import ExportLessonPdf from "@/components/lesson-library/ExportLessonPdf.vue";
import ShareMenu from "@/components/lesson-library/ShareMenu.vue";
import LoginRequiredModal from "@/components/modal/LoginRequiredModal.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ProfessionalDevelopmentComments from "@/components/professional-development/ProfessionalDevelopmentComments.vue";
import ViewProfessionalDevelopmentSidebar from "@/components/professional-development/ViewProfessionalDevelopmentSidebar.vue";
import BreadCrumbs from "@/components/common/BreadCrumbs.vue";
import { createProfessionalDevelopmentCommentService } from "@/services/commentServiceFactory";
import cookiesService from "@/services/cookiesService";
import professionalDevelopmentService from "@/services/professionalDevelopmentService";
import { setupSlideThumbnailHandlers } from "@/tiptap-extensions/slide-deck/slideModalHelper";
import { getApiError } from "@/utils/apiError";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { setDocumentTitle } from "@/utils/pageTitle";
import {
  BookmarkIcon,
  CommentVariantIcon,
  ShareIcon,
} from "@/components/icons";
import { useDisplay } from "vuetify";
import { addTableHeaders } from "@/utils/addTableHeaders";

const route = useRoute();
const router = useRouter();
const { mobile } = useDisplay();
const isMobile = computed(() => mobile.value);
const activeTabIndex = ref(0);
const sidebarCollapsed = ref(false);
const showMobileSidebar = ref(false);
const showMobileMenu = ref(false);
const lessonTabContentRef = ref(null);
const isLoading = ref(true);
const isCommentingEnabled = ref(false);
const currentLessonId = ref(null);
const isAuthenticated = ref(false);
const isCopySlideModalOpen = ref(false);
const currentCopyResource = ref({ url: "", name: "" });
const showScormIframe = ref(false);
const scormIframeUrl = ref("");
const isLoadingScormUrl = ref(false);
const isScormIframeLoading = ref(false);
const showLoginRequiredModal = ref(false);
const loadError = ref(null);

const commentService = createProfessionalDevelopmentCommentService();

// Mobile sidebar function
const handleShowMobileSidebar = () => {
  showMobileSidebar.value = true;
  showMobileMenu.value = false; // Close the menu when showing sidebar
};

// Toggle comments function for mobile menu
const toggleComments = () => {
  isCommentingEnabled.value = !isCommentingEnabled.value;
  // Don't close the menu immediately, let user see the change
};

// Default data structure
const defaultLessonShell = {
  title: null,
  short_description: null,
  duration: null,
  thumbnail: null,
  isLiked: false,
  tagSections: [],
  tabs: [
    {
      name: "Content",
      duration: null,
      sections: [
        {
          content: "",
        },
      ],
    },
  ],
};

const categories = ref([]);
const software = computed(() => {
  const softwareCat = categories.value.find(
    (category) => category.name === "software"
  );
  let softwareTerms = null;
  if (softwareCat) {
    softwareTerms = softwareCat.terms.map((term) => term.title).join(", ");
  }

  return softwareTerms;
});

const breadcrumbItems = computed(() => [
  { label: "Professional Development", to: "/professional-development" },
  { label: lessonData.value.title || "Untitled Lesson", active: true },
]);

const lessonData = ref(JSON.parse(JSON.stringify(defaultLessonShell)));

const commentsByBlockId = ref({});
const showCommentPopover = ref(false);
const activeBlockIdForPopover = ref(null);
const popoverPosition = ref({
  top: 0,
  left: 0,
  arrowSide: "top", // Default, will be updated
  arrowOffset: "20px", // Default, will be updated
});
const currentCommentIconElement = ref(null);
const popoverHostSelector = ref(null); // For Teleport: stores a selector like '#block-id-xyz' or null

const commentsForActiveBlock = computed(() => {
  if (!activeBlockIdForPopover.value) return [];
  return commentsByBlockId.value[activeBlockIdForPopover.value] || [];
});

const loadComments = async () => {
  if (!currentLessonId.value) {
    commentsByBlockId.value = {};
    // nextTick(manageCommentIcons);
    return;
  }
  try {
    const response = await commentService.getContentComments(
      currentLessonId.value
    );
    if (response.data && response.data.data && response.data.data.rows) {
      const newCommentsByBlockId = {};
      response.data.data.rows.forEach((comment) => {
        if (!newCommentsByBlockId[comment.block_id]) {
          newCommentsByBlockId[comment.block_id] = [];
        }
        newCommentsByBlockId[comment.block_id].push({
          id: comment.id,
          text: comment.comment,
          author: comment.user ? comment.user.fullname : "Anonymous",
          avatar: comment.user ? comment.user.image : null,
          date: comment.created_at,
          userId: comment.user_id,
          isLiked: comment.isLiked,
          likeCount: comment.like_count,
        });
      });
      console.log("newCommentsByBlockId", newCommentsByBlockId);
      commentsByBlockId.value = newCommentsByBlockId;
    } else {
      commentsByBlockId.value = {};
    }
  } catch (error) {
    console.error("Error loading comments:", error);
    commentsByBlockId.value = {};
  }
  // nextTick(manageCommentIcons);
};

const handlePageClickClearTooltip = (event) => {
  if (showCommentPopover.value) {
    const popoverElement = document.querySelector(".comment-popover");
    // Check if the click is on the current comment icon
    const clickedOnCurrentIcon =
      currentCommentIconElement.value &&
      currentCommentIconElement.value.contains(
        event.target.closest(".comment-icon-wrapper")
      );

    // Multiple ways to check if click is inside popover
    const clickedInsidePopover1 = event.target.closest(".comment-popover");
    const clickedInsidePopover2 =
      popoverElement && popoverElement.contains(event.target);
    const clickedInsidePopover3 = event.target.matches(
      ".comment-popover, .comment-popover *"
    );

    // Check if click is within popover host element (since it's teleported)
    const clickedInsidePopoverHost =
      popoverHostSelector.value &&
      popoverHostSelector.value.contains(event.target);

    // Coordinate-based check as ultimate fallback
    let clickedInsidePopoverBounds = false;
    if (popoverElement) {
      const rect = popoverElement.getBoundingClientRect();
      const clickX = event.clientX;
      const clickY = event.clientY;
      clickedInsidePopoverBounds =
        clickX >= rect.left &&
        clickX <= rect.right &&
        clickY >= rect.top &&
        clickY <= rect.bottom;
    }

    // Check if the click is inside the delete modal
    const clickedInsideDeleteModal = event.target.closest(
      ".delete-modal-overlay"
    );

    // Combined check - if ANY method detects it's inside the popover, don't close
    const clickedInsidePopover =
      clickedInsidePopover1 ||
      clickedInsidePopover2 ||
      clickedInsidePopover3 ||
      clickedInsidePopoverHost ||
      clickedInsidePopoverBounds;

    // Don't close if clicking inside the popover, delete modal, or current icon
    if (
      clickedInsidePopover ||
      clickedInsideDeleteModal ||
      clickedOnCurrentIcon
    ) {
      return;
    }

    // Close the popover if clicking outside
    closeCommentPopover();
  }
};

// Function to calculate popover position when teleported
function calculateTeleportedPopoverPosition(iconElement, hostElement) {
  if (!iconElement || !hostElement) return null;

  const iconRect = iconElement.getBoundingClientRect();
  const hostRect = hostElement.getBoundingClientRect();

  const popoverWidth = 350; // Must match CommentPopover.vue CSS width
  const popoverHeight = 450; // Must match CommentPopover.vue CSS max-height

  let popoverViewportTop, popoverViewportLeft, arrowSide, arrowOffsetValue;

  // Horizontal placement: Default to the right of the icon (viewport coordinates)
  popoverViewportLeft = iconRect.right + 15;
  arrowSide = "left"; // Popover is to the right, arrow points left

  // If it goes off-screen to the right (viewport), place it to the left
  if (popoverViewportLeft + popoverWidth > window.innerWidth - 20) {
    popoverViewportLeft = iconRect.left - popoverWidth - 15;
    arrowSide = "right"; // Popover is to the left, arrow points right
  }

  // Vertical placement: Attempt to vertically center the popover with the icon (viewport coordinates)
  const iconCenterY = iconRect.top + iconRect.height / 2;
  popoverViewportTop = iconCenterY - popoverHeight / 2;

  if (popoverViewportTop < 20) {
    popoverViewportTop = 20;
  } else if (popoverViewportTop + popoverHeight > window.innerHeight - 20) {
    popoverViewportTop = window.innerHeight - 20 - popoverHeight;
  }

  if (arrowSide === "left" || arrowSide === "right") {
    const iconCenterRelativeToPopoverViewportTop =
      iconCenterY - popoverViewportTop;
    arrowOffsetValue = `${Math.max(
      10,
      Math.min(popoverHeight - 30, iconCenterRelativeToPopoverViewportTop)
    )}px`;
  } else {
    const iconCenterXRelativeToPopoverViewportLeft =
      iconRect.left + iconRect.width / 2 - popoverViewportLeft;
    arrowOffsetValue = `${Math.max(
      10,
      Math.min(popoverWidth - 30, iconCenterXRelativeToPopoverViewportLeft)
    )}px`;
  }

  const relativeTop = popoverViewportTop - hostRect.top;
  const relativeLeft = popoverViewportLeft - hostRect.left;

  return {
    top: relativeTop,
    left: relativeLeft,
    arrowSide: arrowSide,
    arrowOffset: arrowOffsetValue,
  };
}

const handleOpenCommentPopover = (blockId, iconElement, event) => {
  if (!isCommentingEnabled.value) return;

  if (activeBlockIdForPopover.value === blockId && showCommentPopover.value) {
    closeCommentPopover(); // Use the centralized close function
    return;
  }

  const hostElement = iconElement.closest("[data-block-id]");
  if (!hostElement) {
    console.error(
      "Could not find host [data-block-id] element for comment icon.",
      iconElement
    );
    return;
  }

  popoverHostSelector.value = hostElement;

  activeBlockIdForPopover.value = blockId;
  currentCommentIconElement.value = iconElement;

  const newPosition = calculateTeleportedPopoverPosition(
    iconElement,
    hostElement
  );
  if (newPosition) {
    popoverPosition.value = newPosition;
  }

  showCommentPopover.value = true;
};

const handleCommentsUpdated = async () => {
  // Reload comments from the server to keep data in sync
  await loadComments();
  // Re-apply comment icons and highlighting
  // nextTick(() => {
  //   manageCommentIcons();
  // });
};

const closeCommentPopover = () => {
  console.log("closing comment popover");
  showCommentPopover.value = false;
  activeBlockIdForPopover.value = null;
  currentCommentIconElement.value = null;
  popoverHostSelector.value = null; // Clear the teleport target
};

const openCopySlideModal = () => {
  currentCopyResource.value = {
    url: lessonData.value.slide_url,
    name: "Slide Deck",
  };
  isCopySlideModalOpen.value = true;
};

const openCopyResourceModal = (material) => {
  currentCopyResource.value = {
    url: material.url,
    name: material.name,
  };
  isCopySlideModalOpen.value = true;
};

const errorActions = computed(() => [
  {
    label: "Back to Professional Development",
    icon: "mdi-arrow-left",
    variant: "secondary",
    handler: () => router.push({ path: "/professional-development" }),
  },
  {
    label: "Retry",
    icon: "mdi-refresh",
    variant: "primary",
    handler: () => fetchLessonData(),
  },
]);

const viewScormFile = async () => {
  // Check if user is authenticated first
  if (!isAuthenticated.value) {
    showLoginRequiredModal.value = true;
    return;
  }

  if (!currentLessonId.value) {
    console.error("Cannot view SCORM: Lesson ID not available.");
    return;
  }

  isLoadingScormUrl.value = true;
  try {
    const response = await professionalDevelopmentService.viewScorm(
      currentLessonId.value
    );
    const url = response.data?.data;
    if (url) {
      scormIframeUrl.value = url;
      showScormIframe.value = true;
      isScormIframeLoading.value = true;
    }
  } catch (error) {
    console.error("Error loading SCORM URL:", error);
  } finally {
    isLoadingScormUrl.value = false;
  }
};

const scormIframeLoaded = () => {
  isScormIframeLoading.value = false;
};

const closeScormIframe = async () => {
  showScormIframe.value = false;
  scormIframeUrl.value = "";

  // Refetch data after closing SCORM modal to get updated progress
  await fetchLessonData();
};

// Extract data fetching logic into a reusable function
const fetchLessonData = async () => {
  const lessonSlug = route.params.slug;

  if (!lessonSlug) {
    isLoading.value = false;
    currentLessonId.value = null;
    commentsByBlockId.value = {};
    lessonData.value = JSON.parse(JSON.stringify(defaultLessonShell));
    categories.value = [];
    loadError.value = {
      message: "Professional development not found",
      code: 404,
    };
    return;
  }

  try {
    isLoading.value = true;
    loadError.value = null;
    currentLessonId.value = null;
    commentsByBlockId.value = {};

    const response = await professionalDevelopmentService.getBySlug(lessonSlug);

    if (response.data && response.data.data) {
      const apiData = response.data.data;
      currentLessonId.value = apiData.id;

      lessonData.value.id = apiData.id;
      lessonData.value.title = apiData.title;
      lessonData.value.short_description = apiData.short_description;
      lessonData.value.duration = apiData.duration;
      lessonData.value.thumbnail = apiData.thumbnail;
      lessonData.value.isLiked = apiData.isLiked;
      categories.value = apiData.categories;
      lessonData.value.slide_url = apiData.slide_url;
      lessonData.value.lesson_instructional = apiData.lesson_instructional;
      lessonData.value.progress = apiData.progress;

      // Set the document title to the professional development title
      if (lessonData.value.title) {
        setDocumentTitle(lessonData.value.title);
      }

      const contents = JSON.parse(apiData.content);
      if (contents && contents.length > 0) {
        lessonData.value.tabs = contents;
      }
      lessonData.value.tagSections = [];

      await loadComments();
      nextTick(() => {
        initializeTabFromUrl();

        if (lessonTabContentRef.value) {
          runDomObserver();
        }
      });
    } else {
      lessonData.value = JSON.parse(JSON.stringify(defaultLessonShell));
      currentLessonId.value = null;
      commentsByBlockId.value = {};
      categories.value = [];
      loadError.value = {
        message: "Professional development not found",
        code: 404,
      };
      setDocumentTitle("Professional Development");
    }
  } catch (error) {
    const { message, code } = getApiError(
      error,
      "Failed to load professional development. Please try again."
    );
    lessonData.value = JSON.parse(JSON.stringify(defaultLessonShell));
    currentLessonId.value = null;
    commentsByBlockId.value = {};
    categories.value = [];
    loadError.value = { message, code };
  } finally {
    isLoading.value = false;
  }
};

const likeLesson = async () => {
  if (!isAuthenticated.value) {
    return;
  }

  if (!currentLessonId.value) {
    console.error("Cannot like lesson: Lesson ID not available.");
    return;
  }

  try {
    const originalIsLiked = lessonData.value.isLiked;
    lessonData.value.isLiked = !originalIsLiked;

    const response = await professionalDevelopmentService.likeLesson(
      currentLessonId.value
    );

    if (response.status !== 200) {
      lessonData.value.isLiked = originalIsLiked;
      console.error("API error liking lesson:", response);
    }
  } catch (error) {
    console.error("Error liking lesson:", error);
    lessonData.value.isLiked = !lessonData.value.isLiked;
  }
};

let domObserver = null;
let isManagingCommentIcons = false;

const manageCommentIcons = () => {
  if (isManagingCommentIcons) {
    return; // Prevent recursive calls
  }

  isManagingCommentIcons = true;

  // Temporarily disconnect the observer to prevent infinite loops
  const wasObserverActive = domObserver !== null;
  if (wasObserverActive && domObserver) {
    domObserver.disconnect();
  }

  nextTick(() => {
    if (!lessonTabContentRef.value) {
      isManagingCommentIcons = false;
      return;
    }

    const allTrackedBlocks = Array.from(
      lessonTabContentRef.value.querySelectorAll("[data-block-id]")
    );

    allTrackedBlocks.forEach((blockElement) => {
      const existingIcon = blockElement.querySelector(".comment-icon-wrapper");
      if (existingIcon) {
        if (existingIcon.__clickHandler__) {
          existingIcon.removeEventListener(
            "click",
            existingIcon.__clickHandler__
          );
          delete existingIcon.__clickHandler__;
        }
        existingIcon.remove();
      }
      const unmarkBlockContent = (targetBlockElement) => {
        try {
          const marks = Array.from(
            targetBlockElement.querySelectorAll("mark.comment-highlight")
          );
          marks.forEach((markNode) => {
            const parent = markNode.parentNode;
            if (parent && parent.nodeType === Node.ELEMENT_NODE) {
              // Create a document fragment to safely move children
              const fragment = document.createDocumentFragment();
              while (markNode.firstChild) {
                fragment.appendChild(markNode.firstChild);
              }
              // Insert the fragment before the mark node
              if (parent.contains(markNode)) {
                console.log("parent", parent);
                parent?.insertBefore(fragment, markNode);
                parent.removeChild(markNode);
              }
            }
          });

          // Handle case where the target element itself is a mark
          if (
            targetBlockElement.tagName === "MARK" &&
            targetBlockElement.classList.contains("comment-highlight")
          ) {
            const parent = targetBlockElement.parentNode;
            if (
              parent &&
              parent.nodeType === Node.ELEMENT_NODE &&
              parent.contains(targetBlockElement)
            ) {
              const fragment = document.createDocumentFragment();
              while (targetBlockElement.firstChild) {
                fragment.appendChild(targetBlockElement.firstChild);
              }
              parent?.insertBefore(fragment, targetBlockElement);
              parent.removeChild(targetBlockElement);
            }
          }

          // Normalize the parent to clean up any text nodes
          if (
            targetBlockElement.parentNode &&
            targetBlockElement.parentNode.nodeType === Node.ELEMENT_NODE
          ) {
            targetBlockElement.parentNode.normalize();
          }
        } catch (error) {
          // Silently handle DOM manipulation errors that can occur during cleanup
          console.warn("Error during comment highlight cleanup:", error);
        }
      };
      unmarkBlockContent(blockElement);
    });

    if (!isCommentingEnabled.value) {
      isManagingCommentIcons = false;
      // Reconnect observer if it was active
      if (wasObserverActive && lessonTabContentRef.value) {
        // reconnectDomObserver();
      }
      return;
    }

    allTrackedBlocks.forEach((block) => {
      const blockId = block.getAttribute("data-block-id");
      let parent = block.parentElement;
      let isNestedInCommentable = false;
      while (parent && parent !== lessonTabContentRef.value) {
        if (parent.hasAttribute("data-block-id")) {
          isNestedInCommentable = true;
          break;
        }
        parent = parent.parentElement;
      }
      if (isNestedInCommentable) {
        return;
      }
      if (
        block.innerHTML.trim() === "" ||
        block.innerHTML.trim() === "&nbsp;"
      ) {
        return;
      }

      let iconWrapper = document.createElement("div");
      const imageElement = document.createElement("img");
      imageElement.src = "/images/icon/comment.svg";
      imageElement.alt = "Comment";
      iconWrapper.className = "comment-icon-wrapper";
      iconWrapper.appendChild(imageElement);
      iconWrapper.setAttribute("data-target-block-id", blockId);

      if (block.classList.contains("slide-deck-container")) {
        iconWrapper.style.top = "10px";
        iconWrapper.style.right = "20px";
      }

      if (getComputedStyle(block).position === "static") {
        block.style.position = "relative";
      }

      const clickHandler = (event) => {
        event.stopPropagation();
        const targetBlockId = event.currentTarget.getAttribute(
          "data-target-block-id"
        );
        handleOpenCommentPopover(targetBlockId, event.currentTarget, event);
      };
      iconWrapper.addEventListener("click", clickHandler);
      iconWrapper.__clickHandler__ = clickHandler;

      const hasComments =
        commentsByBlockId.value[blockId] &&
        commentsByBlockId.value[blockId].length > 0;

      if (hasComments) {
        iconWrapper.classList.add("has-comments");
        const markTextRecursively = (currentElement) => {
          if (
            currentElement.classList &&
            currentElement.classList.contains("comment-icon-wrapper")
          ) {
            return;
          }
          const leafTextContainers = [
            "P",
            "SPAN",
            "H1",
            "H2",
            "H3",
            "H4",
            "H5",
            "H6",
            "A",
            "EM",
            "STRONG",
            "CODE",
            "S",
            "U",
            "I",
            "B",
            "FIGCAPTION",
            "BLOCKQUOTE",
            "LI",
          ];
          let canWrapSelf =
            leafTextContainers.includes(currentElement.tagName) &&
            !currentElement.querySelector("[data-block-id]") &&
            currentElement.textContent.trim() !== "";

          if (canWrapSelf) {
            let alreadyMarkedByUs = false;
            if (
              currentElement.children.length === 1 &&
              currentElement.firstElementChild &&
              currentElement.firstElementChild.tagName === "MARK" &&
              currentElement.firstElementChild.classList.contains(
                "comment-highlight"
              )
            ) {
              alreadyMarkedByUs = true;
            }
            if (!alreadyMarkedByUs) {
              const directChildIsMark =
                currentElement.children.length === 1 &&
                currentElement.firstElementChild?.tagName === "MARK";
              const isAlreadyAMark = currentElement.tagName === "MARK";
              if (
                !directChildIsMark &&
                !isAlreadyAMark &&
                !currentElement.classList.contains("comment-item")
              ) {
                currentElement.innerHTML = `<mark class="comment-highlight">${currentElement.innerHTML}</mark>`;
              }
            }
          } else {
            Array.from(currentElement.children).forEach((child) => {
              if (
                !child.classList ||
                !child.classList.contains("comment-icon-wrapper")
              ) {
                markTextRecursively(child);
              }
            });
          }
        };
        markTextRecursively(block);
        block.appendChild(iconWrapper);
      } else {
        iconWrapper.classList.remove("has-comments");
        block.appendChild(iconWrapper);
      }
    });

    isManagingCommentIcons = false;

    // Reconnect observer if it was active
    if (wasObserverActive && lessonTabContentRef.value) {
      // reconnectDomObserver();
    }
  });
};

const reconnectDomObserver = () => {
  if (!lessonTabContentRef.value) return;

  domObserver = new MutationObserver((mutationsList) => {
    let needsAccordionInit = false;
    let needsSlideHandlerInit = false;
    let needsIconManagement = false;

    for (const mutation of mutationsList) {
      if (mutation.type === "childList") {
        needsIconManagement = true;
        for (const node of Array.from(mutation.addedNodes).concat(
          Array.from(mutation.removedNodes)
        )) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (
              node.matches(".tooltip-block") ||
              node.querySelector(".tooltip-block")
            ) {
              needsAccordionInit = true;
            }
            if (
              node.matches(".slide-deck-container") ||
              node.querySelector(".slide-deck-container")
            ) {
              needsSlideHandlerInit = true;
            }
            if (
              node.hasAttribute("data-block-id") ||
              node.querySelector("[data-block-id]")
            ) {
              needsIconManagement = true;
            }
          }
        }
      } else if (
        mutation.type === "attributes" &&
        (mutation.attributeName === "data-block-id" ||
          mutation.attributeName === "class")
      ) {
        needsIconManagement = true;
      }
    }

    if (needsAccordionInit || needsSlideHandlerInit || needsIconManagement) {
      nextTick(() => {
        if (needsAccordionInit && lessonTabContentRef.value) {
          initAccordions(lessonTabContentRef.value);
        }
        if (needsSlideHandlerInit && lessonTabContentRef.value) {
          setupSlideThumbnailHandlers(lessonTabContentRef.value);
        }
        // if (
        //   needsIconManagement &&
        //   lessonTabContentRef.value &&
        //   !isManagingCommentIcons
        // ) {
        //   manageCommentIcons();
        // }
      });
    }
  });

  domObserver.observe(lessonTabContentRef.value, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["data-block-id", "class", "style"],
  });
};

const currentTab = computed(() => {
  if (
    !lessonData.value.tabs ||
    activeTabIndex.value >= lessonData.value.tabs.length
  ) {
    return { type: null, assessments: [], sections: [] };
  }
  return lessonData.value.tabs[activeTabIndex.value];
});

const getCurrentTabSections = () => {
  if (
    lessonData.value.tabs &&
    lessonData.value.tabs.length > 0 &&
    activeTabIndex.value < lessonData.value.tabs.length
  ) {
    return lessonData.value.tabs[activeTabIndex.value].sections || [];
  }
  return [];
};

// Function to handle tab selection with URL synchronization
const selectTab = (index) => {
  activeTabIndex.value = index;
  updateUrlWithTab(index);
};

// Function to update URL with tab parameter
const updateUrlWithTab = (tabIndex) => {
  // Handle Comments tab (which is at index equal to tabs length)
  if (tabIndex === lessonData.value.tabs.length) {
    router.push({
      query: {
        ...route.query,
        tab: "comments",
      },
    });
    return;
  }

  // Handle regular content tabs
  if (lessonData.value.tabs && lessonData.value.tabs[tabIndex]) {
    const tabName = lessonData.value.tabs[tabIndex].name;
    // Convert tab name to URL-friendly format (lowercase, replace spaces with hyphens)
    const tabSlug = tabName
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    router.push({
      query: {
        ...route.query,
        tab: tabSlug,
      },
    });
  } else {
    // Remove tab query parameter if invalid index
    const { tab, ...otherQuery } = route.query;
    router.push({ query: otherQuery });
  }
};

// Function to initialize tab from URL query parameter
const initializeTabFromUrl = () => {
  const tabQuery = route.query.tab;
  if (tabQuery) {
    // Handle Comments tab
    if (tabQuery === "comments") {
      activeTabIndex.value = lessonData.value.tabs.length;
      return;
    }

    // Handle regular content tabs
    if (lessonData.value.tabs && lessonData.value.tabs.length > 0) {
      const tabIndex = lessonData.value.tabs.findIndex((tab) => {
        const tabSlug = tab.name
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-]/g, "");
        return tabSlug === tabQuery;
      });

      if (tabIndex !== -1) {
        activeTabIndex.value = tabIndex;
      } else {
        // If tab not found, default to first tab and clean URL
        activeTabIndex.value = 0;
        updateUrlWithTab(0);
      }
    }
  } else {
    // If no tab query, default to first tab
    activeTabIndex.value = 0;
  }
};

function initAccordions(container) {
  if (!container) return;
  const tooltipBlocks = container.querySelectorAll(".tooltip-block");
  tooltipBlocks.forEach((block) => {
    block.classList.remove("is-open");
    block.removeAttribute("data-open");

    const titleLine = block.querySelector(".tooltip-title-line");
    const bodyWrapper = block.querySelector(".tooltip-body-wrapper");
    const arrowEl = block.querySelector(".accordion-arrow");

    if (bodyWrapper) {
      bodyWrapper.classList.remove("is-open");
    }
    if (arrowEl) {
      arrowEl.classList.remove("is-open");
    }

    if (titleLine && bodyWrapper) {
      const titleEl = block.querySelector(".tooltip-title");
      const bodyEl = block.querySelector(".tooltip-body");
      if (titleEl) titleEl.setAttribute("contenteditable", "false");
      if (bodyEl) bodyEl.setAttribute("contenteditable", "false");

      const existingHandler = titleLine.__accordionClickHandler__;
      if (existingHandler) {
        titleLine.removeEventListener("click", existingHandler);
      }

      function toggleAccordion() {
        const isOpen = block.classList.contains("is-open");
        if (isOpen) {
          block.classList.remove("is-open");
          bodyWrapper.classList.remove("is-open");
          if (arrowEl) arrowEl.classList.remove("is-open");
          block.removeAttribute("data-open");
        } else {
          block.classList.add("is-open");
          bodyWrapper.classList.add("is-open");
          if (arrowEl) arrowEl.classList.add("is-open");
          block.setAttribute("data-open", "");
        }
      }
      titleLine.addEventListener("click", toggleAccordion);
      titleLine.__accordionClickHandler__ = toggleAccordion;
    }
  });
}

const runDomObserver = () => {
  const contentArea = lessonTabContentRef.value;

  if (contentArea) {
    const initializeContent = () => {
      let initialized = false;

      const performInitialization = () => {
        if (initialized) return;
        initialized = true;
        initAccordions(lessonTabContentRef.value);
        setupSlideThumbnailHandlers(lessonTabContentRef.value);
        // manageCommentIcons();
      };

      const waitForTooltips = (attempts = 0, maxAttempts = 20) => {
        if (initialized || attempts >= maxAttempts) {
          if (attempts >= maxAttempts && !initialized) {
            performInitialization();
          }
          return;
        }

        const tooltipBlocks =
          lessonTabContentRef.value?.querySelectorAll(".tooltip-block");
        const hasTooltips = tooltipBlocks && tooltipBlocks.length > 0;

        if (hasTooltips) {
          performInitialization();
        } else {
          setTimeout(() => waitForTooltips(attempts + 1, maxAttempts), 100);
        }
      };

      const contentObserver = new MutationObserver((mutations) => {
        if (initialized) {
          contentObserver.disconnect();
          return;
        }

        for (const mutation of mutations) {
          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            const tooltipBlocks =
              lessonTabContentRef.value?.querySelectorAll(".tooltip-block");
            if (tooltipBlocks && tooltipBlocks.length > 0) {
              contentObserver.disconnect();
              performInitialization();
              return;
            }
          }
        }
      });

      nextTick(() => {
        if (lessonTabContentRef.value) {
          contentObserver.observe(lessonTabContentRef.value, {
            childList: true,
            subtree: true,
          });
        }
        waitForTooltips();

        setTimeout(() => {
          contentObserver.disconnect();
        }, 5000);
      });
    };

    initializeContent();

    // Use the new reconnectDomObserver function
    if (typeof MutationObserver !== "undefined") {
      reconnectDomObserver();
    }
  }
};

onMounted(async () => {
  // Set a sensible fallback while loading
  setDocumentTitle("Professional Development");

  // Use the extracted function instead of duplicating code
  await fetchLessonData();

  // Check authentication
  const token = await cookiesService.getCookie("authToken");
  if (token) {
    isAuthenticated.value = true;
  }

  window.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  if (domObserver) {
    domObserver.disconnect();
  }
  if (lessonTabContentRef.value) {
    const commentableBlocks = lessonTabContentRef.value.querySelectorAll(
      ".comment-icon-wrapper"
    );
    commentableBlocks.forEach((iconWrapper) => {
      if (iconWrapper.__clickHandler__) {
        iconWrapper.removeEventListener("click", iconWrapper.__clickHandler__);
        delete iconWrapper.__clickHandler__;
      }
    });
    const tooltipBlocks = lessonTabContentRef.value.querySelectorAll(
      ".tooltip-title-line"
    );
    tooltipBlocks.forEach((titleLine) => {
      if (titleLine.__accordionClickHandler__) {
        titleLine.removeEventListener(
          "click",
          titleLine.__accordionClickHandler__
        );
        delete titleLine.__accordionClickHandler__;
      }
    });
  }
  window.removeEventListener("keydown", handleEscKey);
  document.body.classList.remove("body-no-scroll");
});

const handleEscKey = (event) => {
  if (event.key === "Escape" && showCommentPopover.value) {
    console.log("call from handleEscKey");
    closeCommentPopover();
  }
};

watch(activeTabIndex, () => {
  nextTick(() => {
    if (lessonTabContentRef.value) {
      initAccordions(lessonTabContentRef.value);
      manageCommentIcons();
      setupSlideThumbnailHandlers(lessonTabContentRef.value);
    }
  });
});

// Watch for route query changes (tab parameter)
watch(
  () => route.query.tab,
  (newTab, oldTab) => {
    if (
      newTab !== oldTab &&
      lessonData.value.tabs &&
      lessonData.value.tabs.length > 0
    ) {
      initializeTabFromUrl();
    }
  }
);

// Watch for route changes (different lesson)
watch(
  () => route.params.slug,
  async (newSlug, oldSlug) => {
    if (newSlug && newSlug !== oldSlug) {
      showCommentPopover.value = false;
      activeBlockIdForPopover.value = null;
      currentCommentIconElement.value = null;
      activeTabIndex.value = 0;

      // Clear tab query parameter when navigating to different lesson
      const { tab, ...otherQuery } = route.query;
      if (tab) {
        router.replace({ query: otherQuery });
      }

      // Disconnect old observer if it exists
      if (domObserver) {
        domObserver.disconnect();
        domObserver = null;
      }

      // Use the extracted function instead of duplicating code
      await fetchLessonData();
    }
  }
);

watch(showCommentPopover, (isPopoverVisible) => {
  if (isPopoverVisible) {
    document.body.classList.add("body-no-scroll");
    // For Teleport, initial position is set on open.
    // We might need a nextTick if the hostElement's layout isn't stable immediately.
    // However, calculateTeleportedPopoverPosition uses getBoundingClientRect, which should be fine.
    // No scroll listener needed here for Teleport.
  } else {
    document.body.classList.remove("body-no-scroll");
    // No scroll listener removal needed here.
    // Clearing popoverHostSelector is handled in closeCommentPopover
  }
});

watch(isCommentingEnabled, (newValue) => {
  manageCommentIcons();
  if (!newValue && showCommentPopover.value) {
    console.log("call from watch isCommentingEnabled");
    closeCommentPopover();
  }
});

// Watch for when lesson tabs are actually loaded
watch(
  () => lessonData.value.tabs,
  (newTabs, oldTabs) => {
    const hasRealContent =
      newTabs &&
      newTabs.length > 0 &&
      newTabs.some(
        (tab) =>
          tab.sections &&
          tab.sections.length > 0 &&
          tab.sections.some(
            (section) => section.content && section.content.trim() !== ""
          )
      );

    if (hasRealContent) {
      const waitForContentRef = (attempts = 0, maxAttempts = 10) => {
        if (attempts >= maxAttempts) {
          return;
        }

        if (lessonTabContentRef.value) {
          runDomObserver();
        } else {
          setTimeout(() => waitForContentRef(attempts + 1, maxAttempts), 100);
        }
      };

      nextTick(() => {
        nextTick(() => {
          waitForContentRef();
        });
      });
    }
  },
  { deep: true }
);

// Keep document title in sync with professional development title
watch(
  () => lessonData.value.title,
  (newTitle) => {
    if (newTitle && typeof newTitle === "string" && newTitle.trim().length) {
      setDocumentTitle(newTitle);
    }
  }
);
</script>

<style scoped>
:deep([data-block-id]) {
  position: relative;
  padding-right: 20px;
}

:deep(.comment-icon-wrapper) {
  position: absolute;
  top: 2px;
  right: 2px;
  cursor: pointer;
  font-size: 18px;
  color: #aaa;
  padding: 2px;
  display: none;
  transition:
    color 0.2s ease,
    opacity 0.2s ease;
  z-index: 5;
  opacity: 0.6;
}

:deep([data-block-id]:hover .comment-icon-wrapper) {
  display: inline-block;
  opacity: 1;
}

:deep(.comment-icon-wrapper.has-comments) {
  color: var(--primary-blue, #007bff);
  opacity: 1;
}

.lesson-preview-container {
  margin: 0 auto;
  padding: 35px 0 35px 35px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 40px);
}

.lesson-content-wrapper {
  display: flex;
  align-items: flex-start;
}

.lesson-main-content {
  flex: 1;
  width: 100%;
  min-width: 0;
}

.lesson-header {
  padding-bottom: 20px;
  margin-bottom: 20px;
}

.lesson-header-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;
}

.lesson-title-container {
  width: 100%;
}

.comment-toggle-container {
  background-color: var(--white-hover-1);
  padding: 4px;
  border-radius: 100px;
}

.lesson-meta-container {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.lesson-meta-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--primary-blue);
}

.lesson-meta-item-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lesson-meta-item-icon img {
  width: 100%;
  height: 100%;
}

.like-icon,
.share-icon-wrap {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--white-hover-1);
  border-radius: 50%;
  padding: 7px;
}

.lesson-title {
  font-size: var(--fs-3xl);
  margin: 0 0 10px 0;
  color: var(--primary-blue);
}

.lesson-meta {
  display: flex;
  gap: 20px;
  color: #777;
  align-items: center;
  margin-top: 40px;
}

.lesson-meta-wrapper {
  display: flex;
  gap: 10px;
}

.lesson-actions-wrapper {
  display: flex;
  align-items: center;
  gap: 20px;
}

.lesson-duration {
  display: flex;
  align-items: center;
  gap: 5px;
}

.lesson-main-container {
  display: flex;
  position: relative;
  flex: 1;
}

/* Removed sidebar related styles */

.lesson-content {
  flex: 1;
  transition: all 0.3s ease;
  border-radius: 8px;
  background-color: #fff;
  margin-right: 20px;
}

.lesson-content.expanded {
  margin-right: 0;
}

.lesson-tabs {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  padding-bottom: 40px;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
  font-size: var(--fs-big);
}

.lesson-tab {
  padding: 8px 20px;
  cursor: pointer;
  color: var(--black-body-text);
  position: relative;
  transition: all 0.2s ease;
  white-space: nowrap;
  border-bottom: 1px solid var(--neutral-700);
}

.lesson-tab:hover {
  color: var(--primary-pink, #ff5a5f);
  border-bottom-color: var(--primary-pink, #ff5a5f);
}

.lesson-tab.active {
  border-bottom: 3px solid var(--primary-pink, #ff5a5f);
  color: var(--primary-pink, #ff5a5f);
  font-weight: bold;
}

.tab-duration {
  font-size: 0.8em;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -30px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.lesson-tab-content {
  min-height: 300px;
  /* Or adjust as needed */
}

.lesson-section {
  margin-bottom: 30px;
}

.empty-section-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  border-radius: 12px;
  text-align: center;
  transition: all 0.3s ease;
  min-height: 200px;
}

.empty-section-icon {
  margin-bottom: 16px;
  opacity: 0.6;
  transition: all 0.3s ease;
}

.empty-section-state:hover .empty-section-icon {
  opacity: 0.8;
  transform: translateY(-2px);
}

.empty-section-text h3 {
  color: #6c757d;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  letter-spacing: -0.025em;
}

.empty-section-text p {
  color: #adb5bd;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
  max-width: 400px;
}

/* Content styles are now handled globally in tiptap-editor.css */
.tiptap-content table {
  display: table !important;
  width: 100% !important;
  max-width: 100% !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
  border-radius: 16px !important;
  overflow: hidden !important;
  margin-bottom: 1rem !important;
}

.tiptap-content table tr:first-child td,
.tiptap-content table tr:first-child th {
  background-color: var(--primary-blue) !important;
  color: var(--white) !important;
  font-size: var(--fs-large) !important;
  font-weight: 500 !important;
  line-height: 28px !important;
  padding: 20px 10px !important;
  border: none !important;
  border-bottom: 1px solid var(--neutral-300) !important;
  border-top: 1px solid var(--neutral-300) !important;
}

.tiptap-content table td,
.tiptap-content table th {
  min-width: 120px !important;
  white-space: normal !important;
  padding: 20px 10px !important;
  border: none !important;
  border-bottom: 1px solid var(--neutral-300) !important;
  font-size: var(--fs-medium) !important;
  font-weight: 400 !important;
  line-height: 24px !important;
  color: var(--black-body-text) !important;
  vertical-align: top !important;
  background-color: transparent !important;
}

.tiptap-content table td:first-child,
.tiptap-content table th:first-child {
  border-left: 1px solid var(--neutral-300) !important;
}

.tiptap-content table td:last-child,
.tiptap-content table th:last-child {
  border-right: 1px solid var(--neutral-300) !important;
}

@media (max-width: 768px) {
  .tiptap-content table {
    display: block !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
  }
}

.tiptap-content h2 {
  margin-top: 0;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.resources-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: fit-content;
  max-width: 100%;
  margin-top: 20px;
}

.resources-section h2 {
  margin: 0 0 12px;
  color: #333;
  font-size: 1.5em;
}

.resources-buttons {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: fit-content;
  max-width: 100%;
}

.resource-copy-button {
  width: fit-content;
  max-width: 100%;
  min-height: 48px;
  padding: 10px 16px !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-big);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-title-big);
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.resource-copy-button :deep(.v-btn__content) {
  min-width: 0;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-title-big);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-title-big);
  letter-spacing: 0;
  white-space: normal;
}

.resource-copy-button :deep(.v-icon--start) {
  margin-inline-start: 0;
  margin-inline-end: 8px;
}

.resource-copy-button--primary {
  background-color: var(--primary-blue, #003d6a) !important;
  color: var(--white, #ffffff) !important;
}

.resource-copy-button--secondary {
  border: 1px solid var(--primary-blue, #003d6a);
  background-color: var(--white, #ffffff) !important;
  color: var(--primary-blue, #003d6a) !important;
}

.action-buttons {
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: #333;
  font-weight: 500;
}

.back-button:hover {
  background-color: #e0e0e0;
}

/* Mobile Details Menu */
.mobile-details-menu {
  display: none;
}

.mobile-details-btn {
  color: var(--primary-blue, #1976d2) !important;
  background-color: rgba(25, 118, 210, 0.08) !important;
}

.mobile-menu-card {
  min-width: 280px;
  border: 1px solid #e0e0e0;
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  user-select: none;
}

.mobile-menu-item:hover {
  background-color: rgba(25, 118, 210, 0.04);
}

.menu-item-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.menu-item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: rgba(25, 118, 210, 0.08);
  border-radius: 50%;
}

.menu-item-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.menu-item-title {
  font-weight: 500;
  font-size: 0.95rem;
  color: #1a1a1a;
  line-height: 1.2;
}

.menu-item-subtitle {
  font-size: 0.8rem;
  color: #666;
  line-height: 1.2;
}

/* Responsive visibility classes */
.mobile-only {
  display: none !important;
}

.desktop-only {
  display: flex !important;
}

/* Enhanced Mobile Responsiveness */
@media (max-width: 768px) {
  .lesson-preview-container {
    padding: 15px 10px 15px 15px;
    min-height: calc(100vh - 20px);
  }

  .mobile-only {
    display: flex !important;
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-details-menu {
    display: block !important;
  }

  .lesson-header-content {
    flex-direction: column;
    gap: 0px;
  }

  .title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .lesson-title {
    font-size: var(--fs-xxl);
    line-height: 1.2;
    margin: 0;
  }

  .lesson-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    margin-top: 10px;
  }

  .lesson-meta-wrapper.desktop-only {
    display: none !important;
  }

  .lesson-actions-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
  }

  .software-item {
    font-size: var(--fs-small);
    color: var(--primary-blue);
    font-weight: var(--font-weight-medium);
  }

  .mobile-sticky-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    background-color: white;
    box-shadow: 0px -4px 15px rgba(0, 0, 0, 0.05);
    z-index: 100;
    display: flex;
    justify-content: center;
    border-top: 1px solid var(--neutral-300);
  }

  .mobile-details-btn-full {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid var(--primary-blue);
    border-radius: 12px;
    color: var(--primary-blue);
    font-family: var(--font-family-primary);
    font-weight: var(--font-weight-bold);
    font-size: var(--fs-title-big);
    width: 100%;
    padding: 10px 16px;
    min-height: 48px;
    background: transparent;
  }

  .info-icon-img {
    width: 24px;
    height: 24px;
  }

  /* Gray Switch */
  .switch-gray .slider {
    background-color: var(--neutral-300);
  }
  .switch-gray input:checked + .slider {
    background-color: var(--neutral-500);
  }
  .action-comment-toggle {
    background-color: var(--white-hover-1);
    border-radius: 100px;
    padding: 4px 8px;
    gap: 8px;
  }

  .lesson-cover-image {
    width: 100%;
    height: 180px;
  }

  .lesson-main-container {
    flex-direction: column-reverse;
  }

  .lesson-content {
    width: 100%;
    margin-right: 0;
  }

  .lesson-content.expanded {
    margin-right: 0;
  }

  .mobile-divider {
    margin: 0px 5px;
  }

  .lesson-tabs {
    overflow-x: auto;
    white-space: nowrap;
    padding-bottom: 15px;
    gap: 15px;
    margin-bottom: 20px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    /* Firefox */
    -ms-overflow-style: none;
    /* Internet Explorer 10+ */
  }

  .lesson-tabs::-webkit-scrollbar {
    display: none;
    /* WebKit */
  }

  .lesson-tab {
    display: inline-block;
    min-width: fit-content;
    padding: 10px 15px;
    font-size: 0.9rem;
  }

  .tab-duration {
    font-size: 0.75rem;
    bottom: -25px;
  }

  .lesson-tab-content {
    padding: 0 5px;
  }

  .tiptap-content h2 {
    font-size: 1.5em;
  }

  .tiptap-content h3 {
    font-size: 1.3em;
  }

  .tiptap-content p {
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .resources-section {
    width: 100%;
  }

  .resources-section h2 {
    font-size: 1.4rem;
  }

  .resources-buttons {
    width: 100%;
  }

  .resource-copy-button {
    width: 100%;
    justify-content: center !important;
  }

  /* Comment icon adjustments for mobile */
  :deep([data-block-id]) {
    padding-right: 0;
  }

  :deep(.comment-icon-wrapper) {
    right: 5px;
    top: 5px;
    padding: 4px;
    opacity: 0.8;
  }

  :deep([data-block-id]:hover .comment-icon-wrapper),
  :deep(.comment-icon-wrapper.has-comments) {
    display: inline-block;
    opacity: 1;
  }

  /* Empty section state mobile adjustments */
  .empty-section-state {
    padding: 40px 15px;
    min-height: 160px;
  }

  .empty-section-text h3 {
    font-size: 1.1rem;
  }

  .empty-section-text p {
    font-size: 0.9rem;
  }

  .lesson-meta-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    margin-top: 0px;
  }

  /* Touch-friendly like and share buttons */
  .lesson-meta-item {
    min-height: 44px;
    padding: 0;
    border-radius: 8px;
    transition: background-color 0.2s ease;
  }

  .lesson-meta-item:hover,
  .lesson-meta-item:focus {
    background-color: #f5f5f5;
  }

  /* Switch component mobile optimization */
  .switch {
    width: 45px;
    height: 22px;
  }

  .slider:before {
    height: 16px;
    width: 16px;
    left: 3px;
    bottom: 3px;
  }

  input:checked + .slider:before {
    transform: translateX(23px);
  }
}

/* Small mobile devices */
@media (max-width: 480px) {
  .lesson-preview-container {
    padding: 10px 5px 10px 10px;
  }

  .lesson-tabs {
    gap: 10px;
    margin-bottom: 15px;
  }

  .lesson-tab {
    padding: 8px 12px;
    font-size: 0.85rem;
    font-size: var(--fs-medium);
  }

  .lesson-meta {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 0px;
  }

  .lesson-meta > div:first-child {
    gap: 2px;
  }

  .lesson-meta-item {
    font-size: 0.9rem;
  }

  .comment-toggle-container {
    font-size: 0.9rem;
  }

  .mobile-menu-card {
    min-width: 260px;
  }

  .menu-item-title {
    font-size: 0.9rem;
  }

  .menu-item-subtitle {
    font-size: 0.75rem;
  }

  .tiptap-content {
    font-size: 0.9rem;
  }

  .tiptap-content h2 {
    font-size: 1.3em;
  }

  .tiptap-content h3 {
    font-size: 1.15em;
  }

  .resource-copy-button {
    padding: 10px 12px !important;
  }
}

/* Tablet-specific adjustments */
@media (min-width: 769px) and (max-width: 1024px) {
  .lesson-preview-container {
    padding: 25px 15px 25px 25px;
  }

  .lesson-title {
    font-size: 2.2em;
  }

  .lesson-content.expanded {
    margin-right: 40px;
  }
}

/* Extra large screen */
@media (min-width: 2560px) {
  .lesson-preview-container {
    max-width: 2400px;
  }
}

.comment-toggle-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: var(--primary-blue, #007bff);
}

input:focus + .slider {
  box-shadow: 0 0 1px var(--primary-blue, #007bff);
}

input:checked + .slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 24px;
}

.slider.round:before {
  border-radius: 50%;
}

/* SCORM Modal Styles */
.scorm-iframe-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.scorm-iframe-container {
  width: 100%;
  height: 100%;
  background-color: white;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

.scorm-iframe-header {
  display: flex;
  justify-content: flex-end;
  position: absolute;
  top: 0;
  left: 0;
  right: 20px;
  padding: 10px;
  z-index: 1000;
  background-color: transparent;
}

.scorm-iframe-modal iframe {
  flex: 1;
  width: 100%;
  height: 100%;
  border: none;
}

.scorm-iframe-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 5;
}

.scorm-iframe-loading p {
  margin-top: 15px;
  color: #333;
  font-weight: 500;
}

.scorm-loader {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid var(--primary-blue, #1976d2);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.button-loader {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>

<style>
/* Global style for body-no-scroll */
.body-no-scroll {
  overflow: hidden !important;
}
</style>
